import { CookieKey, DEFAULT_EXPIRE_DAYS } from '@/constants';
import type { TAuthResponse } from '@/fetchers/umsAPI/umsAuthenticator.fetchers';
import Cookies from 'js-cookie';
import { forceSignOut } from './forceSignOut';
import { getRefreshCookieExpiry } from './refreshCookieExpiry';
import { isRejectedRefresh } from './tokenRefreshError';
import { refreshToken as refreshTokenFetcher } from './tokenRefresher';

/**
 * Promise to track the current token refresh operation
 * When it's null, no refresh is in progress
 * When it's a Promise, it represents the current refresh operation
 */
let refreshPromise: Promise<string> | null = null;

// One exclusive lock name shared by every tab of the site
const RENEWAL_LOCK_NAME = 'pmc-token-renewal';

/**
 * Wait for the current refresh to finish, if there is one.
 * A failed refresh is not rethrown here: the waiting request just goes on and handles its own 401.
 */
export const getRefreshPromise = async () => refreshPromise?.catch(() => undefined) ?? null;

/**
 * Run `task` while holding the site-wide renewal lock, so renewals in different tabs queue up.
 * Browsers without the Web Locks API run it directly.
 */
const withRenewalLock = async <T>(task: () => Promise<T>): Promise<T> =>
  typeof navigator !== 'undefined' && navigator.locks ? navigator.locks.request(RENEWAL_LOCK_NAME, task) : task();

/**
 * Store the renewed tokens: the access token like at sign-in, and the refresh token only when
 * pm_ums rotated it. Without a rotated token the existing refresh cookie keeps its value and expiry.
 */
const storeRenewedTokens = ({ access_token, refresh_token }: Pick<TAuthResponse, 'access_token' | 'refresh_token'>) => {
  Cookies.set(CookieKey.AccessToken, access_token, { expires: DEFAULT_EXPIRE_DAYS });
  if (refresh_token) {
    Cookies.set(CookieKey.RefreshToken, refresh_token, { expires: getRefreshCookieExpiry(refresh_token) });
  }
};

const renew = async (tokenToSend: string): Promise<string> => {
  const response = await refreshTokenFetcher(tokenToSend);
  storeRenewedTokens(response);
  return response.access_token;
};

/**
 * The renewal itself, run while holding the lock:
 * 1. Reads the refresh token from the cookie (after acquiring the lock, so a tab that waited sends the
 *    token the previous holder stored); `suppliedToken` is only for contexts without cookies
 * 2. Renews and stores the new tokens
 * 3. When rejected and the cookie now holds a different token (another tab or request rotated it), renews once more with it
 * 4. When still rejected, ends the session: clears the signed-in browser state and reloads as a guest
 *
 * Any failure other than a rejection (5xx, 429, network) removes nothing and does not reload.
 */
const runRenewal = async (suppliedToken?: string): Promise<string> =>
  withRenewalLock(async () => {
    const sent = Cookies.get(CookieKey.RefreshToken) || suppliedToken;
    if (!sent) {
      throw new Error('No refresh token available');
    }

    try {
      try {
        return await renew(sent);
      } catch (error) {
        const current = Cookies.get(CookieKey.RefreshToken);
        if (!isRejectedRefresh(error) || !current || current === sent) throw error;
        return await renew(current);
      }
    } catch (error) {
      if (isRejectedRefresh(error)) {
        await forceSignOut();
      }
      throw error;
    }
  });

/**
 * Renew the session. Callers in the same tab share one in-flight renewal.
 *
 * @returns Promise<string> - New access token
 * @throws TokenRefreshError when pm_ums answers non-OK, or Error for any other failure
 */
export const renewSession = async (suppliedToken?: string): Promise<string> => {
  if (!refreshPromise) {
    const promise: Promise<string> = runRenewal(suppliedToken).finally(() => {
      if (refreshPromise === promise) refreshPromise = null;
    });
    refreshPromise = promise;
  }
  return refreshPromise;
};

interface OriginalRequestInfo {
  url: string;
  config: RequestInit;
}

export const handleApiError = async (
  error: Response,
  originalRequestInfo: OriginalRequestInfo,
  refreshToken?: string,
): Promise<Response> => {
  // Only handle 401 errors - all others should be handled by the calling code
  if (error.status !== 401) {
    throw error;
  }

  /*
   * Check for refresh token - if none exists, we can't refresh
   * Use custom refresh token if provided, otherwise use the one from cookies
   *
   * This is because we couldn't get refresh token from cookie in server side
   * and we need to pass it through the request
   */
  const _refreshToken = refreshToken || Cookies.get(CookieKey.RefreshToken);

  if (!_refreshToken) {
    Cookies.remove(CookieKey.AccessToken);
    throw error;
  }

  try {
    // Shares the renewal already in flight, if any
    const newToken = await renewSession(_refreshToken);

    // Retry the original request with the new token
    return await fetch(originalRequestInfo.url, {
      ...originalRequestInfo.config,
      headers: {
        ...originalRequestInfo.config.headers,
        Authorization: `Bearer ${newToken}`,
      },
    });
  } catch (_) {
    // If anything fails, throw the original error
    throw error;
  }
};
